import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { watchlistAPI } from '../services/api';
import { HiOutlineFilm, HiOutlineStar, HiOutlineCalendar } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w342';

const MobileTop10 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [monthData, setMonthData] = useState({ month: 0, year: 0 });
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [activeTab, setActiveTab] = useState('movies');

  useEffect(() => {
    fetchTop10();
  }, []);

  const fetchTop10 = async () => {
    try {
      setLoading(true);
      const response = await watchlistAPI.getTop10Monthly();
      const data = response.data;
      setMonthData({ month: data.month, year: data.year });
      setMovies(data.movies);
      setSeries(data.series);
    } catch (error) {
      console.error('Error fetching top 10:', error);
      toast.error(t('common.error', 'Erro'));
    } finally {
      setLoading(false);
    }
  };

  const formatMonthYear = () => {
    const monthNames = [
      t('settings.monthJan', 'Janeiro'),
      t('settings.monthFeb', 'Fevereiro'),
      t('settings.monthMar', 'Março'),
      t('settings.monthApr', 'Abril'),
      t('settings.monthMay', 'Maio'),
      t('settings.monthJun', 'Junho'),
      t('settings.monthJul', 'Julho'),
      t('settings.monthAug', 'Agosto'),
      t('settings.monthSep', 'Setembro'),
      t('settings.monthOct', 'Outubro'),
      t('settings.monthNov', 'Novembro'),
      t('settings.monthDec', 'Dezembro')
    ];
    if (monthData.month === 0) return '';
    return `${monthNames[monthData.month - 1]} ${monthData.year}`;
  };

  const monthYear = formatMonthYear();
  const currentItems = activeTab === 'movies' ? movies : series;
  const moviesLabel = t('navigation.movies');
  const seriesLabel = t('navigation.series');

  const getPosterUrl = (path) => {
    if (!path) return '/default-poster.png';
    return `${TMDB_IMAGE_BASE}${path}`;
  };

  const getRankStyle = (rank) => {
    if (rank === 1) return 'mt10__rank--gold';
    if (rank === 2) return 'mt10__rank--silver';
    if (rank === 3) return 'mt10__rank--bronze';
    return 'mt10__rank--default';
  };

  const renderList = (items) => {
    if (items.length === 0) return null;

    return (
      <div className="mt10__list">
        {items.map((item, idx) => (
          <motion.div
            key={item.tmdb_id}
            className={`mt10__list-item ${item.rank === 1 ? 'mt10__list-item--gold' : item.rank === 2 ? 'mt10__list-item--silver' : item.rank === 3 ? 'mt10__list-item--bronze' : ''}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <div className={`mt10__list-rank ${getRankStyle(item.rank)}`}>
              {item.rank}
            </div>
            <div className="mt10__list-poster">
              <img
                src={getPosterUrl(item.poster_path)}
                alt={item.title}
                loading="lazy"
                onError={(e) => { e.target.src = '/default-poster.png'; }}
              />
            </div>
            <div className="mt10__list-info">
              <h4 className="mt10__list-title">{item.title}</h4>
              <div className="mt10__list-meta">
                {item.vote_average > 0 && (
                  <span className="mt10__star">
                    <HiOutlineStar size={11} />
                    {item.vote_average.toFixed(1)}
                  </span>
                )}
                <span className="mt10__adds">
                  {item.count} {item.count === 1 ? t('top10.added', 'add') : t('top10.addedPlural', 'adds')}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  return (
    <div className="mt10">
      {/* Header */}
      <div className="mt10__header">
        <h1>{t('top10.title', 'Top 10')}</h1>
        <div className="mt10__month">
          <HiOutlineCalendar size={14} />
          <span>{monthYear}</span>
        </div>
      </div>

      {/* Segmented Control iOS */}
      <div className="mt10__segment">
        <button
          className={`mt10__segment-btn ${activeTab === 'movies' ? 'mt10__segment-btn--active' : ''}`}
          onClick={() => setActiveTab('movies')}
        >
          <HiOutlineFilm size={16} />
          {moviesLabel}
          {movies.length > 0 && <span className="mt10__segment-count">{movies.length}</span>}
        </button>
        <button
          className={`mt10__segment-btn ${activeTab === 'series' ? 'mt10__segment-btn--active' : ''}`}
          onClick={() => setActiveTab('series')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="7" width="20" height="15" rx="2" ry="2"/>
            <polyline points="17 2 12 7 7 2"/>
          </svg>
          {seriesLabel}
          {series.length > 0 && <span className="mt10__segment-count">{series.length}</span>}
        </button>
      </div>

      {/* Content */}
      <div className="mt10__content">
        {loading ? (
          <div className="mt10__loading">
            <div className="spinner"></div>
            <p>{t('common.loading', 'Carregando...')}</p>
          </div>
        ) : currentItems.length === 0 ? (
          <div className="mt10__empty">
            <HiOutlineFilm size={48} />
            <p>{t('top10.noData', 'Nenhum conteúdo adicionado neste mês')}</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderList(currentItems)}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

    </div>
  );
};

export default MobileTop10;
